import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { default as api, type Purchase } from '../service/api';
import { createPersistStorage } from './utils/persist';

interface ManagerPurchasesState {
  purchases: Purchase[];
  isLoading: boolean;
  error: string | null;
}

interface ManagerPurchasesActions {
  fetchPurchases: () => Promise<void>;
  approvePurchase: (purchaseId: string) => Promise<void>;
  deletePurchase: (purchaseId: string) => Promise<void>;
  clearPurchases: () => void;
}

type ManagerPurchasesStore = ManagerPurchasesState & ManagerPurchasesActions;

type ManagerPurchasesStorePersist = Pick<ManagerPurchasesStore, 'purchases'>;

const useManagerPurchasesStore = create<ManagerPurchasesStore>()(
  persist<
    ManagerPurchasesStore,
    [],
    [],
    ManagerPurchasesStorePersist
  >((set, get) => ({
      purchases: [],
      isLoading: false,
      error: null,

      fetchPurchases: async () => {
        const hasCachedPurchases = get().purchases.length > 0;
        set({ isLoading: !hasCachedPurchases, error: null });

        try {
          const purchases = await api.purchasesGetAll();
          set({
            purchases,
            isLoading: false,
          });
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : 'Failed to fetch purchases',
            isLoading: false,
          });
        }
      },

      approvePurchase: async (purchaseId: string) => {
        try {
          const updatedPurchase = await api.purchaseApprove(purchaseId);

          // Update the purchase in state
          set(state => ({
            purchases: state.purchases.map(purchase =>
              purchase._id === purchaseId ? updatedPurchase : purchase,
            ),
          }));
        } catch (error) {
          throw new Error(
            error instanceof Error
              ? error.message
              : 'Failed to approve purchase',
          );
        }
      },

      deletePurchase: async (purchaseId: string) => {
        try {
          await api.purchaseDelete(purchaseId);

          // Remove purchase from state
          set(state => ({
            purchases: state.purchases.filter(
              purchase => purchase._id !== purchaseId,
            ),
          }));
        } catch (error) {
          throw new Error(
            error instanceof Error
              ? error.message
              : 'Failed to delete purchase',
          );
        }
      },

      clearPurchases: () =>
        set({
          purchases: [],
          error: null,
        }),
    }),
    {
      name: 'manager-purchases-store',
      storage: createPersistStorage<ManagerPurchasesStorePersist>(),
      partialize: state => ({
        purchases: state.purchases,
      }),
    },
  ),
);

export default useManagerPurchasesStore;
